import React, {
  useEffect,
  Fragment,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react'
import { Dialog, Transition } from '@headlessui/react'
import Headline from '@atoms/Headline/Headline'
import * as S from './AlertBox.styles'
import * as T from './AlertBox.types'

export const DefaultCloseIcon = () => {
  return (
    <svg viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M6.513 7.947l-4.94 4.94L2.987 14.3l4.94-4.94 5.013 5.013 1.414-1.414L9.34 7.947l4.96-4.96-1.414-1.414-4.96 4.96L3.04 1.646 1.626 3.06l4.887 4.887z'
        fill='currentColor'
        fillRule='evenodd'
      />
    </svg>
  )
}

const AlertBox = forwardRef(
  (
    {
      title,
      type = 'error',
      position = 'center',
      description,
      children = '',
      extendedWrapperStyle = '',
      wrapperStyleOverride,
      positionStyleOverride,
      autoCloseTime,
      customHeadline,
      closeButtonIcon,
      forceWrapperPosMobile = false,
      showBackdrop = true,
      backdropColor = 'bg-black',
      backdropOpacity = 'opacity-50',
      headlineColor = 'text-white',
      readOnly,
      onOpen,
      onClose
    }: T.AlertBoxPropTypes,
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)

    const handleClose = () => {
      if (!readOnly) {
        onClose && onClose()
        setIsOpen(false)
      }
    }

    const handleOpen = () => {
      onOpen && onOpen()
      setIsOpen(true)
    }

    const handleAutoClose = () => {
      if (autoCloseTime && isOpen) {
        const timer = setTimeout(() => {
          handleClose()
        }, autoCloseTime)

        return () => clearTimeout(timer)
      }

      return () => false
    }

    useImperativeHandle(ref, () => ({
      showAlert() {
        handleOpen()
      },
      closeAlert() {
        handleClose()
      }
    }))

    useEffect(() => {
      handleAutoClose()
    }, [isOpen])

    const alertStyle = wrapperStyleOverride || S.AlertStyles[type]
    const positionStyle = positionStyleOverride || S.AlertPositions[position]
    const mobilePositionStyle =
      !wrapperStyleOverride && forceWrapperPosMobile
        ? S.AlertPositions.mobilePosition
        : ''

    return (
      <Transition show={isOpen} as={Fragment}>
        <Dialog onClose={() => handleClose()} className='relative z-[9990]'>
          {/* The backdrop, rendered as a fixed sibling to the panel container */}
          {showBackdrop && (
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo={backdropOpacity}
              leave='ease-in duration-200'
              leaveFrom={backdropOpacity}
              leaveTo='opacity-0'
            >
              <div
                className={`fixed inset-0 ${backdropColor}`}
                aria-hidden='true'
              />
            </Transition.Child>
          )}

          {/* Full-screen container to center the panel */}
          <div className='fixed inset-0 flex h-full w-full items-center justify-center sm:p-4'>
            {/* Container to center the panel */}
            <Transition.Child
              as={Fragment}
              enter='transition duration-100 ease-out'
              enterFrom='transform scale-85 opacity-0'
              enterTo='transform scale-100 opacity-100'
              leave='transition duration-75 ease-out'
              leaveFrom='transform scale-100 opacity-100'
              leaveTo='transform scale-85 opacity-0'
            >
              <div
                className={`flex min-h-full w-full overflow-y-auto ${positionStyle}`}
              >
                <Dialog.Panel
                  className={`${alertStyle} ${mobilePositionStyle} ${extendedWrapperStyle}`}
                >
                  <Dialog.Title as={Fragment}>
                    <div className='flex items-center justify-end'>
                      {customHeadline || (
                        <Headline
                          tag='h6'
                          className={`w-full ${headlineColor}`}
                        >
                          {title}
                        </Headline>
                      )}
                      {!readOnly && (
                        <span
                          onClick={() => handleClose()}
                          className='ml-2 flex w-[20px] cursor-pointer'
                        >
                          {closeButtonIcon || <DefaultCloseIcon />}
                        </span>
                      )}
                    </div>
                  </Dialog.Title>
                  <Dialog.Description className='text-16'>
                    {description}
                  </Dialog.Description>
                  {children}
                </Dialog.Panel>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    )
  }
)

AlertBox.displayName = 'AlertBox'

export default AlertBox
